import type { PermissionKey } from '@cuito/types';

export interface NavItem {
  to: string;
  label: string;
  /** 需要的权限；不填表示登录即可见 */
  permission?: PermissionKey;
  /** 供 E2E 断言的稳定选择器 */
  testId: string;
  children?: NavItem[];
}

/**
 * 侧边栏导航定义。
 *
 * 权限取自 `01-requirements.md` §2.3 的矩阵，与后端 `@RequirePermission` 一一对应：
 * 前端隐藏菜单只是体验，真正的拦截在后端。
 */
export const NAV_ITEMS: readonly NavItem[] = [
  { to: '/', label: '工作台', testId: 'nav-dashboard' },
  {
    // 分组项自身不可点击，只作为容器（`to` 为空串）
    to: '',
    label: '商品管理',
    testId: 'nav-catalog',
    children: [
      { to: '/products', label: '商品', permission: 'product:read', testId: 'nav-products' },
      {
        to: '/promotion-schemes',
        label: '促销方案',
        permission: 'product:read',
        testId: 'nav-promotion-schemes',
      },
    ],
  },
  { to: '/stock', label: '库存', permission: 'stock:read', testId: 'nav-stock' },
  {
    // 分组项自身不可点击，只作为容器（`to` 为空串）。
    // 仓库也是 `StockLocation`（`03-data-model.md` §4.2），因此并入「地点」而不是单列一项。
    to: '',
    label: '地点',
    testId: 'nav-place',
    children: [
      {
        // `GET /locations` 需要 `stock:read`（`04-api-contract.md:801`），
        // 不是 `shop:read`——按接口要求的权限显示，菜单才不会点进去就 403。
        to: '/locations',
        label: '全部地点',
        permission: 'stock:read',
        testId: 'nav-locations',
      },
      {
        to: '/warehouses',
        label: '仓库',
        permission: 'warehouse:read',
        testId: 'nav-warehouses',
      },
      // Phase 8 Task 14：展会（存档）与「我报名的展会」。两条都要 `event:read`
      // （`GET /events` 与 `GET /participations` 的鉴权列都是 `event:read`，契约 §11 端点表 1/9 行）。
      { to: '/events', label: '展会', permission: 'event:read', testId: 'nav-events' },
      {
        to: '/participations',
        label: '我报名的展会',
        permission: 'event:read',
        testId: 'nav-participations',
      },
    ],
  },
  { to: '/orders', label: '订单', permission: 'order:accept', testId: 'nav-orders' },
  { to: '/ledger', label: '账本', permission: 'ledger:read', testId: 'nav-ledger' },
  { to: '/consignments', label: '寄售台账', permission: 'consign:read', testId: 'nav-consignments' },
  { to: '/settlements', label: '结算', permission: 'settlement:read', testId: 'nav-settlements' },
  { to: '/reports', label: '报表', permission: 'report:read', testId: 'nav-reports' },
  {
    to: '/audit-logs',
    label: '审计日志',
    permission: 'audit:read',
    testId: 'nav-audit-logs',
  },
  {
    // 分组项自身不可点击，只作为容器（`to` 为空串）
    to: '',
    label: '账户管理',
    testId: 'nav-account',
    children: [
      { to: '/shops', label: '门店管理', permission: 'shop:read', testId: 'nav-shops' },
      { to: '/members', label: '成员管理', permission: 'member:read', testId: 'nav-members' },
      { to: '/settings', label: '社团设置', permission: 'tenant:read', testId: 'nav-settings' },
    ],
  },
];

export interface NavContext {
  /**
   * 当前主体拥有的全部权限。
   * 传 `null` 表示「全通过」（主账号 `OWNER`），与后端语义一致。
   */
  permissions: readonly PermissionKey[] | null;
}

/**
 * 按权限过滤导航树。
 *
 * 抽成纯函数（而非写在组件里）是为了**能单测**：
 * `07-roadmap.md` §1.2 的验收「custom_a 登录后侧边栏中门店入口不渲染」
 * 因此可以用单元测试覆盖，而不必跑 Playwright。
 *
 * 行为：
 *   - 叶子项按 `permission` 过滤
 *   - 分组项在其**全部子项被过滤后**一并消失（不留空分组）
 */
export function filterNav(items: readonly NavItem[], ctx: NavContext): NavItem[] {
  const can = (permission?: PermissionKey): boolean => {
    if (!permission) return true;
    if (ctx.permissions === null) return true;
    return ctx.permissions.includes(permission);
  };

  const result: NavItem[] = [];
  for (const item of items) {
    if (item.children && item.children.length > 0) {
      const children = filterNav(item.children, ctx);
      if (children.length > 0) result.push({ ...item, children });
      continue;
    }
    if (can(item.permission)) result.push(item);
  }
  return result;
}
